/**
 * SidebarContext
 *
 * Context for sharing sidebar state across components.
 * Allows input components to respond to sidebar expansion.
 *
 * CRITICAL: Context value is memoized to prevent infinite re-render loops.
 * Without memoization, every render creates a new context value object,
 * causing all consumers to re-render, which triggers parent re-renders,
 * creating an infinite loop.
 */

import type React from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

type SidebarMode = 'small' | 'normal' | 'expanded';

interface SidebarContextValue {
  sidebarMode: SidebarMode;
  setSidebarMode: (mode: SidebarMode) => void;
  isExpanded: boolean;
}

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined);

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('normal');

  // CRITICAL: Memoize context value to prevent infinite re-render loops
  // Without this, a new object is created on every render, causing all
  // consumers (like WorkflowStatusSidebar) to re-render infinitely
  const value = useMemo<SidebarContextValue>(
    () => ({
      sidebarMode,
      setSidebarMode,
      isExpanded: sidebarMode === 'expanded',
    }),
    [sidebarMode] // Only recreate when sidebarMode actually changes
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
